"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get FPE_ANSWERS_MAX_BYTES () {
        return FPE_ANSWERS_MAX_BYTES;
    },
    get FpeAnswersSchema () {
        return FpeAnswersSchema;
    },
    get FpeCompleteRequestSchema () {
        return FpeCompleteRequestSchema;
    },
    get FpeCompleteResponseSchema () {
        return FpeCompleteResponseSchema;
    },
    get FpeGetResponseSchema () {
        return FpeGetResponseSchema;
    },
    get FpeRecordSchema () {
        return FpeRecordSchema;
    },
    get FpeSaveRequestSchema () {
        return FpeSaveRequestSchema;
    },
    get FpeSaveResponseSchema () {
        return FpeSaveResponseSchema;
    },
    get FpeStartResponseSchema () {
        return FpeStartResponseSchema;
    },
    get FpeStatusSchema () {
        return FpeStatusSchema;
    },
    get FpeStatusSummarySchema () {
        return FpeStatusSummarySchema;
    },
    get FpeVitalsSchema () {
        return FpeVitalsSchema;
    },
    get FpeVitalsSourceSchema () {
        return FpeVitalsSourceSchema;
    }
});
const _zod = require("zod");
const _fpeformdescriptor = require("./fpe-form-descriptor");
const _fperegistry = require("./fpe-registry");
const _encounterdetail = require("../encounters/encounter-detail");
const _vitals = require("../encounters/vitals");
/*
 * YAKAP First Patient Encounter (FPE) DTOs (HLX-82, ADR-0020).
 *
 * PHI: `answers`, `vitals`, the patient-to-encounter link and a patient's FPE
 * status are PHI. Never log them, put them in span attributes or metric
 * labels, or copy them into audit `details`. Every object is strict.
 */ const isoDateTime = ()=>_zod.z.string().datetime({
        offset: true
    });
const versionNumber = ()=>_zod.z.number({
        message: 'version must be a number.'
    }).int({
        message: 'version must be a whole number.'
    }).min(1, {
        message: 'version must be at least 1.'
    });
const FPE_ANSWERS_MAX_BYTES = 65536;
const FpeStatusSchema = _zod.z.enum([
    'draft',
    'completed'
]);
const utf8Length = (value)=>new TextEncoder().encode(JSON.stringify(value)).length;
const FpeAnswersSchema = _zod.z.record(_zod.z.string(), _zod.z.record(_zod.z.string(), _zod.z.unknown(), {
    message: 'Each section must be an object.'
}), {
    message: 'answers must be an object.'
}).superRefine((answers, ctx)=>{
    for (const [sectionKey, section] of Object.entries(answers)){
        const hasBadFieldKey = Object.keys(section).some((key)=>!_fpeformdescriptor.FPE_FIELD_KEY_PATTERN.test(key));
        if (!_fpeformdescriptor.FPE_SECTION_KEY_PATTERN.test(sectionKey) || hasBadFieldKey) {
            ctx.addIssue({
                code: 'custom',
                path: [],
                message: 'answers has an invalid key.'
            });
            return;
        }
    }
    if (utf8Length(answers) > FPE_ANSWERS_MAX_BYTES) {
        ctx.addIssue({
            code: 'custom',
            path: [],
            message: 'answers is too large.'
        });
    }
});
const FpeVitalsSourceSchema = _zod.z.enum([
    'own',
    'encounter',
    'frozen'
]);
const FpeVitalsSchema = _zod.z.object({
    bpSystolic: _zod.z.number().int().nullable(),
    bpDiastolic: _zod.z.number().int().nullable(),
    heartRate: _zod.z.number().int().nullable(),
    respiratoryRate: _zod.z.number().int().nullable(),
    temperatureC: _zod.z.number().nullable(),
    weightKg: _zod.z.number().nullable(),
    heightCm: _zod.z.number().nullable(),
    bmi: _zod.z.number().nullable(),
    source: FpeVitalsSourceSchema,
    recordedAt: isoDateTime().nullable()
}).strict();
const FpeRecordSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    patientId: _zod.z.string().uuid(),
    encounterId: _zod.z.string().uuid().nullable(),
    status: FpeStatusSchema,
    formVersion: _fperegistry.FpeFormVersionSchema,
    provisional: _zod.z.boolean(),
    answers: FpeAnswersSchema,
    vitals: FpeVitalsSchema.nullable(),
    version: versionNumber(),
    pinOnFile: _zod.z.boolean(),
    completedAt: isoDateTime().nullable(),
    completedBy: _encounterdetail.EncounterUserRefSchema.nullable(),
    createdAt: isoDateTime(),
    updatedAt: isoDateTime()
}).strict();
const FpeGetResponseSchema = _zod.z.object({
    record: FpeRecordSchema.nullable(),
    currentFormVersion: _fperegistry.FpeFormVersionSchema
}).strict();
const FpeStatusSummarySchema = _zod.z.object({
    status: _zod.z.enum([
        'none',
        'draft',
        'completed'
    ]),
    id: _zod.z.string().uuid().nullable(),
    completedAt: isoDateTime().nullable(),
    pinOnFile: _zod.z.boolean(),
    provisional: _zod.z.boolean()
}).strict();
const FpeStartResponseSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    version: versionNumber(),
    resumed: _zod.z.boolean()
}).strict();
const FpeSaveRequestSchema = _zod.z.object({
    version: versionNumber(),
    answers: FpeAnswersSchema,
    encounterId: _zod.z.string().uuid({
        message: 'encounterId must be a valid id.'
    }).nullable(),
    vitals: _vitals.VitalsInputSchema.nullable().optional()
}).strict();
const FpeSaveResponseSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    version: versionNumber(),
    updatedAt: isoDateTime(),
    vitals: FpeVitalsSchema.nullable()
}).strict();
const FpeCompleteRequestSchema = _zod.z.object({
    version: versionNumber()
}).strict();
const FpeCompleteResponseSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    status: _zod.z.literal('completed'),
    completedAt: isoDateTime(),
    version: versionNumber()
}).strict();
